// اجزای مشترک AP-ENG-01 (SCR-STAFF-960، 961، 962): مسیرها، کلیدهای Query، فهرست‌های شمارشی، رنگ وضعیت، انتخاب مسئول و خدمت با جستجوی سروری،
// منابع معرفی PAT و تبدیل تاریخ و ساعت محلی به لحظه UTC.
import Autocomplete from '@mui/material/Autocomplete';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import {
  unwrap,
  type EngAssigneeOption,
  type EngConsultationOutcome,
  type EngConsultationType,
  type EngFollowUpOutcome,
  type EngFollowUpPriority,
  type EngFollowUpType,
  type EngLeadLostReason,
  type EngLeadStatus,
  type EngOfferingOption,
} from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { JalaliDatePickerRHF, latinDigits, TextFieldRHF, toBusinessDate, useDisplaySettings, type StatusTone } from '@phoenix/ui';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { localToUtc } from '../messaging/common';
import { useDebounced } from '../patients/common';

export const LEADS_PATH = '/crm/leads';
export const QUEUE_PATH = '/crm/queue';
export const NEW_CONSULTATION_PATH = '/crm/consultations/new';
export const leadPath = (id: string) => `${LEADS_PATH}/${id}`;
export const consultationPath = (id: string) => `/crm/consultations/${id}`;

export const LEAD_STATUSES: readonly NonNullable<EngLeadStatus>[] = ['New', 'Contacted', 'Consulted', 'Quoted', 'Converted', 'Lost'];
export const LOST_REASONS: readonly NonNullable<EngLeadLostReason>[] = ['Price', 'Time', 'Competitor', 'NoResponse', 'NotSuitable', 'Other'];
export const CONSULTATION_TYPES: readonly NonNullable<EngConsultationType>[] = ['InPerson', 'Phone', 'Online'];
export const CONSULTATION_OUTCOMES: readonly NonNullable<EngConsultationOutcome>[] = ['Converted', 'ThinkingAbout', 'NotConverted', 'Cancelled'];
export const FOLLOWUP_TYPES: readonly NonNullable<EngFollowUpType>[] = ['Call', 'Message', 'InternalReminder'];
export const PRIORITIES: readonly NonNullable<EngFollowUpPriority>[] = ['High', 'Normal', 'Low'];
export const FOLLOWUP_OUTCOMES: readonly NonNullable<EngFollowUpOutcome>[] = ['Answered', 'NoAnswer', 'Booked', 'NotInterested', 'CallAgain'];

/** ساعت پیش‌فرض موعد پیگیری (محلی). */
export const DEFAULT_DUE_TIME = '10:00';

export const engKeys = {
  all: (tenantId: string | undefined) => ['engagement', tenantId] as const,
  leads: (tenantId: string | undefined) => ['engagement', tenantId, 'leads'] as const,
  lead: (tenantId: string | undefined, id: string) => ['engagement', tenantId, 'leads', id] as const,
  board: (tenantId: string | undefined) => ['engagement', tenantId, 'leads', 'board'] as const,
  consultation: (tenantId: string | undefined, id: string) => ['engagement', tenantId, 'consultations', id] as const,
  queue: (tenantId: string | undefined) => ['engagement', tenantId, 'queue'] as const,
  followUps: (tenantId: string | undefined) => ['engagement', tenantId, 'followups'] as const,
  sources: (tenantId: string | undefined) => ['engagement', tenantId, 'sources'] as const,
};

export function leadTone(status: EngLeadStatus): StatusTone {
  switch (status) {
    case 'New':
      return 'booked';
    case 'Contacted':
      return 'confirmed';
    case 'Consulted':
      return 'arrived';
    case 'Quoted':
      return 'pending';
    case 'Converted':
      return 'done';
    default:
      return 'cancelled';
  }
}

export function priorityTone(priority: EngFollowUpPriority): StatusTone {
  return priority === 'High' ? 'error' : priority === 'Low' ? 'inactive' : 'booked';
}

/** نام کامل سرنخ ← نام و نام خانوادگی برای ثبت سریع PAT (واژه نخست نام، بقیه نام خانوادگی). */
export function splitName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/);
  return { firstName: parts[0] ?? '', lastName: parts.slice(1).join(' ') };
}

/** «امروز» محلی (yyyy-MM-dd) با جابه‌جایی روز. */
export function localDate(days = 0): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return toBusinessDate(date);
}

/** تاریخ و ساعت محلی منطقه زمانی نمایش ← لحظه UTC. */
export function useLocalInstant(): (date: string, time: string) => string {
  const { timeZone } = useDisplaySettings();
  return (date, time) => localToUtc(date, latinDigits(time), timeZone);
}

/** منابع معرفی فعال (فهرست PAT). */
export function useLeadSources() {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: engKeys.sources(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/engagement/lead-sources')),
    staleTime: 60_000,
  });
}

/** انتخاب مسئول یا مشاور با جستجوی سروری بر نام. */
export function AssigneePicker({
  value,
  onChange,
  labelKey,
  testId,
  disabled,
}: {
  value: EngAssigneeOption | null;
  onChange: (value: EngAssigneeOption | null) => void;
  labelKey: string;
  testId: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation('eng');
  const { tenantApi, session } = useTenantSession();
  const [input, setInput] = useState('');
  const q = useDebounced(input.trim(), 200);
  const options = useQuery({
    queryKey: [...engKeys.all(session?.tenantId), 'assignees', q],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/engagement/assignees', { params: { query: { q: q || undefined, limit: 20 } } })),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const items = options.data ?? [];
  return (
    <Autocomplete<EngAssigneeOption, false, false, false>
      value={value}
      onChange={(_event, next) => onChange(next)}
      inputValue={input}
      onInputChange={(_event, next) => setInput(next)}
      options={value && !items.some((item) => item.membershipId === value.membershipId) ? [value, ...items] : items}
      filterOptions={(list) => list}
      getOptionLabel={(option) => option.name || t('eng:common.unnamed')}
      isOptionEqualToValue={(option, selected) => option.membershipId === selected.membershipId}
      noOptionsText={t('eng:common.no_options')}
      loading={options.isFetching}
      disabled={disabled}
      sx={{ minWidth: 220 }}
      renderInput={(params) => <TextField {...params} size="small" label={t(labelKey)} inputProps={{ ...params.inputProps, 'data-testid': testId }} />}
    />
  );
}

/** انتخاب خدمت، پکیج یا دسته غیرحساس با جستجوی سروری. */
export function OfferingPicker({
  value,
  onChange,
  labelKey,
  testId,
  categories = false,
  packages = false,
  disabled,
}: {
  value: EngOfferingOption | null;
  onChange: (value: EngOfferingOption | null) => void;
  labelKey: string;
  testId: string;
  categories?: boolean;
  packages?: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation('eng');
  const { tenantApi, session } = useTenantSession();
  const [input, setInput] = useState('');
  const q = useDebounced(input.trim(), 200);
  const options = useQuery({
    queryKey: [...engKeys.all(session?.tenantId), 'offerings', q, categories, packages],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/engagement/offerings', { params: { query: { q: q || undefined, categories, packages, limit: 20 } } })),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const items = options.data ?? [];
  return (
    <Autocomplete<EngOfferingOption, false, false, false>
      value={value}
      onChange={(_event, next) => onChange(next)}
      inputValue={input}
      onInputChange={(_event, next) => setInput(next)}
      options={value && !items.some((item) => item.id === value.id) ? [value, ...items] : items}
      filterOptions={(list) => list}
      getOptionLabel={(option) => `${option.name} · ${t(`eng:offering_kind.${option.kind}`)}`}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      noOptionsText={t('eng:common.no_options')}
      loading={options.isFetching}
      disabled={disabled}
      sx={{ minWidth: 260, flex: 1 }}
      renderInput={(params) => <TextField {...params} size="small" label={t(labelKey)} inputProps={{ ...params.inputProps, 'data-testid': testId }} />}
    />
  );
}

/** موعد: تاریخ (ورود و نمایش شمسی) و ساعت محلی در فرم react-hook-form. */
export function DueFields<T extends FieldValues>({
  control,
  dateName,
  timeName,
  testId,
}: {
  control: Control<T>;
  dateName: FieldPath<T>;
  timeName: FieldPath<T>;
  testId: string;
}) {
  return (
    <Stack direction="row" spacing={1} data-testid={testId}>
      <Stack sx={{ flex: 2 }}>
        <JalaliDatePickerRHF control={control} name={dateName} labelKey="eng:common.due_date" required />
      </Stack>
      <TextFieldRHF
        control={control}
        name={timeName}
        labelKey="eng:common.due_time"
        type="time"
        size="small"
        InputLabelProps={{ shrink: true }}
        inputProps={{ 'data-testid': `${testId}-time` }}
        sx={{ flex: 1 }}
      />
    </Stack>
  );
}
